/**
 * Phase 8 · Cluster C-43 — Sourcing connector credentials service.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.7
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.7
 *
 * One row per (org, kind). Powers two surfaces:
 *   - `Settings → Integrations → Sourcing connectors` (list + connect UI).
 *   - The connector implementations in `src/modules/agent/tools/connectors/*`
 *     which read the row to decide their auth path before hitting the
 *     remote network.
 *
 * Security invariants:
 *   - `encrypted_secret` is NEVER selected by the frontend. `list()` and
 *     `get()` both project only the non-secret columns. Decryption is
 *     a responsibility of the Edge Function proxy the connectors will
 *     eventually route through — never the browser.
 *   - `upsert()` writes the secret as base64(plaintext) under the
 *     `encrypted_secret` column name. This is NOT cryptographic protection
 *     — it is obfuscation against casual log leakage and a clear marker
 *     for "swap this to pgsodium when the edge proxy ships". The DON'T in
 *     C-43 explicitly scopes real encryption out of this PR.
 *   - `has_org_role('owner','admin')` is enforced by RLS (migration 108)
 *     on every write path, so a junior seat that tries to upsert will
 *     get a PostgREST 42501 from Supabase. We still defensively guard at
 *     the UI layer so users see a clear message instead of a DB error.
 */
import { supabase } from '../client';

export type SourceConnectorKind =
  | 'linkedin'
  | 'naukri'
  | 'github'
  | 'gitlab'
  | 'discord'
  | 'internal';

export type SourceConnectorAuthKind =
  | 'oauth'
  | 'api_key'
  | 'session_cookie'
  | 'bot_token'
  | 'none';

export type SourceConnectorStatus = 'active' | 'expired' | 'revoked';

/**
 * The row shape the frontend sees. `encryptedSecret` is NEVER included —
 * anyone needing the secret must call the Edge Function proxy (future
 * work, tracked in `src/modules/agent/tools/connectors/*`).
 */
export interface SourceConnectorRow {
  id: string;
  orgId: string;
  kind: SourceConnectorKind;
  authKind: SourceConnectorAuthKind;
  configJson: Record<string, unknown>;
  status: SourceConnectorStatus;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Columns we are willing to surface to the browser. Intentionally omits
 * `encrypted_secret` — the SELECT list is the enforcement point (RLS
 * can't hide a single column).
 */
const SELECT_FIELDS =
  'id, org_id, kind, auth_kind, config_json, status, last_used_at, created_at, updated_at';

interface RawRow {
  id: string;
  org_id: string;
  kind: SourceConnectorKind;
  auth_kind: SourceConnectorAuthKind;
  config_json: Record<string, unknown> | null;
  status: SourceConnectorStatus;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapRow(r: RawRow): SourceConnectorRow {
  return {
    id: r.id,
    orgId: r.org_id,
    kind: r.kind,
    authKind: r.auth_kind,
    configJson: r.config_json ?? {},
    status: r.status,
    lastUsedAt: r.last_used_at,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

/**
 * base64 wrapper used by `upsert` in place of real encryption. The choice
 * is intentional and documented: we want a clear marker to replace once
 * pgsodium ships in the edge-function proxy. `atob` is reserved for the
 * server-side decrypt path — the frontend never un-encodes this.
 */
function wrapSecret(plain: string): string {
  try {
    if (typeof btoa === 'function') return `b64:${btoa(unescape(encodeURIComponent(plain)))}`;
  } catch {
    // Fall through to the unwrapped path below.
  }
  // If btoa is unavailable (very old runtime) we still persist with a
  // distinct prefix so a future migration can detect and re-wrap.
  return `raw:${plain}`;
}

export interface UpsertPayload {
  authKind: SourceConnectorAuthKind;
  /** Raw secret (API key, bearer token, session cookie string). */
  secret: string;
  config?: Record<string, unknown>;
  status?: SourceConnectorStatus;
}

export const sourceConnectorsService = {
  /**
   * List all connector rows for the org, minus the encrypted secret.
   * Safe to call from any role — the SELECT RLS policy on migration 108
   * is org-scoped and read-only.
   */
  async list(orgId: string): Promise<SourceConnectorRow[]> {
    if (!orgId) return [];
    const { data, error } = await supabase
      .from('ggj_b2b_source_connectors')
      .select(SELECT_FIELDS)
      .eq('org_id', orgId)
      .order('kind', { ascending: true });
    if (error) {
      console.warn('[sourceConnectorsService.list] failed', error.message);
      return [];
    }
    return ((data ?? []) as RawRow[]).map(mapRow);
  },

  /**
   * Single-row lookup for one connector kind. Returns null when the org
   * has not configured this connector yet — callers should treat null as
   * "not connected" (the UI shows the Connect CTA, the connector throws
   * a friendly error before reaching the network).
   */
  async get(orgId: string, kind: SourceConnectorKind): Promise<SourceConnectorRow | null> {
    if (!orgId || !kind) return null;
    const { data, error } = await supabase
      .from('ggj_b2b_source_connectors')
      .select(SELECT_FIELDS)
      .eq('org_id', orgId)
      .eq('kind', kind)
      .maybeSingle();
    if (error) {
      console.warn('[sourceConnectorsService.get] failed', error.message);
      return null;
    }
    return data ? mapRow(data as RawRow) : null;
  },

  /**
   * Insert or update one connector's credential. The secret is wrapped
   * before it ever hits the wire (see `wrapSecret`). `internal` does not
   * need a secret — callers passing kind='internal' should send an empty
   * string and `authKind='none'` is enforced by the DB CHECK.
   */
  async upsert(
    orgId: string,
    kind: SourceConnectorKind,
    payload: UpsertPayload,
  ): Promise<void> {
    if (!orgId) throw new Error('orgId is required.');

    const encrypted = kind === 'internal' ? null : wrapSecret(payload.secret ?? '');

    const { error } = await supabase
      .from('ggj_b2b_source_connectors')
      .upsert(
        {
          org_id: orgId,
          kind,
          auth_kind: payload.authKind,
          encrypted_secret: encrypted,
          config_json: payload.config ?? {},
          status: payload.status ?? 'active',
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'org_id,kind' },
      );

    if (error) throw new Error(error.message);
  },

  /**
   * Flip `status` without touching the secret. Used by the UI's
   * "Disconnect" CTA (sets status to 'revoked') and by connectors
   * that detect a 401 on the remote provider (sets 'expired').
   */
  async setStatus(
    orgId: string,
    kind: SourceConnectorKind,
    status: SourceConnectorStatus,
  ): Promise<void> {
    if (!orgId) throw new Error('orgId is required.');
    const { error } = await supabase
      .from('ggj_b2b_source_connectors')
      .update({ status })
      .eq('org_id', orgId)
      .eq('kind', kind);
    if (error) throw new Error(error.message);
  },

  /**
   * Best-effort last-used stamp. Called from the connector's invoke()
   * after a successful remote call. Fire-and-forget — a missed write
   * here must not break the tool result.
   */
  async markUsed(orgId: string, kind: SourceConnectorKind): Promise<void> {
    if (!orgId) return;
    try {
      const { error } = await supabase
        .from('ggj_b2b_source_connectors')
        .update({ last_used_at: new Date().toISOString() })
        .eq('org_id', orgId)
        .eq('kind', kind);
      if (error) {
        console.warn('[sourceConnectorsService.markUsed] failed', error.message);
      }
    } catch (err) {
      console.warn('[sourceConnectorsService.markUsed] unexpected', err);
    }
  },

  /**
   * Fan-out healthcheck used by the Settings page to render the status
   * pill per connector. Each connector decides what "healthy" means —
   * today "the row exists with status=active" is a good-enough proxy;
   * future work is a real ping into each provider via an Edge Function.
   */
  async healthcheckAll(
    orgId: string,
  ): Promise<Record<string, { ok: boolean; reason?: string }>> {
    const rows = await sourceConnectorsService.list(orgId);
    const out: Record<string, { ok: boolean; reason?: string }> = {};
    for (const row of rows) {
      if (row.kind === 'internal') {
        out[row.kind] = { ok: true };
        continue;
      }
      if (row.status === 'active') {
        out[row.kind] = { ok: true };
      } else {
        out[row.kind] = { ok: false, reason: row.status };
      }
    }
    return out;
  },
};
